import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Grid3X3,
  Palette,
  Circle,
  EyeOff,
  AlertCircle,
  Users,
  TrendingUp,
  Settings,
  ChevronDown,
  ChevronUp,
  Plus,
  Pencil,
  Trash2,
  Save,
  Loader2,
  LayoutGrid,
  Slash
} from 'lucide-react';

// Scatter plot canvas component with background support
function MatrixScatterPlot({ 
  data, 
  groupAverages,
  xQuestion, 
  yQuestion, 
  showIndividuals,
  showGroups,
  backgroundType = 'none',
  quadrantConfig,
  diagonalConfig,
  width = 800,
  height = 500
}) {
  const canvasRef = useRef(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  const paddingLeft = 90;
  const paddingRight = 50;
  const paddingTop = 50;
  const paddingBottom = 60;
  const plotWidth = width - paddingLeft - paddingRight;
  const plotHeight = height - paddingTop - paddingBottom;
  
  // Get axis config from questions
  const xMin = xQuestion?.min_value ?? 0;
  const xMax = xQuestion?.max_value ?? 10;
  const yMin = yQuestion?.min_value ?? 0;
  const yMax = yQuestion?.max_value ?? 10;
  
  // Draw the scatter plot
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !xQuestion || !yQuestion) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    
    // Helper to convert data values to canvas coordinates
    const toCanvasX = (val) => paddingLeft + ((val - xMin) / (xMax - xMin)) * plotWidth;
    const toCanvasY = (val) => height - paddingBottom - ((val - yMin) / (yMax - yMin)) * plotHeight;
    
    // Background - default light color
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, width, height);
    
    // Draw background based on type
    if (backgroundType === 'quadrant' && quadrantConfig) {
      const xSplit = paddingLeft + (plotWidth * (quadrantConfig.x_split / 100));
      const ySplit = height - paddingBottom - (plotHeight * (quadrantConfig.y_split / 100));
      
      // Bottom-left quadrant
      ctx.fillStyle = quadrantConfig.bottom_left_color || '#e0f2fe';
      ctx.fillRect(paddingLeft, ySplit, xSplit - paddingLeft, height - paddingBottom - ySplit);
      
      // Bottom-right quadrant
      ctx.fillStyle = quadrantConfig.bottom_right_color || '#dcfce7';
      ctx.fillRect(xSplit, ySplit, width - paddingRight - xSplit, height - paddingBottom - ySplit);
      
      // Top-left quadrant
      ctx.fillStyle = quadrantConfig.top_left_color || '#fef3c7';
      ctx.fillRect(paddingLeft, paddingTop, xSplit - paddingLeft, ySplit - paddingTop);
      
      // Top-right quadrant
      ctx.fillStyle = quadrantConfig.top_right_color || '#fecaca';
      ctx.fillRect(xSplit, paddingTop, width - paddingRight - xSplit, ySplit - paddingTop);
      
      // Draw quadrant dividing lines
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      
      // Vertical line
      ctx.beginPath();
      ctx.moveTo(xSplit, paddingTop);
      ctx.lineTo(xSplit, height - paddingBottom);
      ctx.stroke();
      
      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(paddingLeft, ySplit);
      ctx.lineTo(width - paddingRight, ySplit);
      ctx.stroke();
      
      ctx.setLineDash([]);
      
      // Draw quadrant labels
      ctx.font = 'bold 11px system-ui, sans-serif';
      ctx.fillStyle = '#475569';
      ctx.textAlign = 'center';
      
      const labelPadding = 20;
      // Top-left label
      ctx.fillText(quadrantConfig.top_left_label || 'Keep Satisfied', 
        paddingLeft + (xSplit - paddingLeft) / 2, paddingTop + labelPadding);
      // Top-right label
      ctx.fillText(quadrantConfig.top_right_label || 'Manage Closely', 
        xSplit + (width - paddingRight - xSplit) / 2, paddingTop + labelPadding);
      // Bottom-left label
      ctx.fillText(quadrantConfig.bottom_left_label || 'Monitor', 
        paddingLeft + (xSplit - paddingLeft) / 2, height - paddingBottom - labelPadding);
      // Bottom-right label
      ctx.fillText(quadrantConfig.bottom_right_label || 'Keep Informed', 
        xSplit + (width - paddingRight - xSplit) / 2, height - paddingBottom - labelPadding);
        
    } else if (backgroundType === 'diagonal' && diagonalConfig) {
      // Draw diagonal background regions
      // Diagonal goes from bottom-left to top-right
      ctx.save();
      
      // Above diagonal = upper-left region (Y > X proportionally)
      ctx.beginPath();
      ctx.moveTo(paddingLeft, height - paddingBottom);   // bottom-left
      ctx.lineTo(paddingLeft, paddingTop);                // top-left
      ctx.lineTo(width - paddingRight, paddingTop);       // top-right
      ctx.closePath();
      ctx.fillStyle = diagonalConfig.above_color || '#dcfce7';
      ctx.fill();
      
      // Below diagonal = lower-right region (Y < X proportionally)
      ctx.beginPath();
      ctx.moveTo(paddingLeft, height - paddingBottom);    // bottom-left
      ctx.lineTo(width - paddingRight, paddingTop);       // top-right
      ctx.lineTo(width - paddingRight, height - paddingBottom); // bottom-right
      ctx.closePath();
      ctx.fillStyle = diagonalConfig.below_color || '#fecaca';
      ctx.fill();
      
      // Draw diagonal line from bottom-left to top-right
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(paddingLeft, height - paddingBottom);
      ctx.lineTo(width - paddingRight, paddingTop);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Draw labels
      ctx.font = 'bold 11px system-ui, sans-serif';
      ctx.fillStyle = '#475569';
      ctx.textAlign = 'center';
      
      // Above label (upper-left area)
      ctx.fillText(diagonalConfig.above_label || 'Above Average', 
        paddingLeft + plotWidth * 0.25, paddingTop + 30);
      // Below label (lower-right area)
      ctx.fillText(diagonalConfig.below_label || 'Below Average', 
        paddingLeft + plotWidth * 0.75, height - paddingBottom - 30);
      
      ctx.restore();
    }
    
    // Grid lines
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    
    // Vertical grid lines
    const xSteps = 10;
    for (let i = 0; i <= xSteps; i++) {
      const x = paddingLeft + (plotWidth / xSteps) * i;
      ctx.beginPath();
      ctx.moveTo(x, paddingTop);
      ctx.lineTo(x, height - paddingBottom);
      ctx.stroke();
    }
    
    // Horizontal grid lines
    const ySteps = 10;
    for (let i = 0; i <= ySteps; i++) {
      const y = paddingTop + (plotHeight / ySteps) * i;
      ctx.beginPath();
      ctx.moveTo(paddingLeft, y);
      ctx.lineTo(width - paddingRight, y);
      ctx.stroke();
    }
    
    // Axes
    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 2;
    
    // X axis
    ctx.beginPath();
    ctx.moveTo(paddingLeft, height - paddingBottom);
    ctx.lineTo(width - paddingRight, height - paddingBottom);
    ctx.stroke();
    
    // Y axis
    ctx.beginPath();
    ctx.moveTo(paddingLeft, paddingTop);
    ctx.lineTo(paddingLeft, height - paddingBottom);
    ctx.stroke();
    
    // Axis labels
    ctx.fillStyle = '#334155';
    ctx.font = '12px system-ui, sans-serif';
    ctx.textAlign = 'center';
    
    // X axis label
    ctx.fillText(xQuestion.label || 'X Axis', paddingLeft + plotWidth / 2, height - 10);
    
    // X axis end labels
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillStyle = '#64748b';
    ctx.textAlign = 'left';
    ctx.fillText(xQuestion.left_label || 'Low', paddingLeft, height - paddingBottom + 30);
    ctx.textAlign = 'right';
    ctx.fillText(xQuestion.right_label || 'High', width - paddingRight, height - paddingBottom + 30);
    
    // Y axis label (rotated)
    ctx.save();
    ctx.translate(15, paddingTop + plotHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillStyle = '#334155';
    ctx.textAlign = 'center';
    ctx.fillText(yQuestion.label || 'Y Axis', 0, 0);
    ctx.restore();
    
    // Y axis end labels
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillStyle = '#64748b';
    ctx.textAlign = 'right';
    // Truncate long labels to fit
    const truncLabel = (text, maxWidth) => {
      if (ctx.measureText(text).width <= maxWidth) return text;
      let t = text;
      while (t.length > 0 && ctx.measureText(t + '...').width > maxWidth) t = t.slice(0, -1);
      return t + '...';
    };
    const yLabelMaxW = paddingLeft - 14;
    ctx.fillText(truncLabel(yQuestion.left_label || 'Low', yLabelMaxW), paddingLeft - 8, height - paddingBottom + 4);
    ctx.fillText(truncLabel(yQuestion.right_label || 'High', yLabelMaxW), paddingLeft - 8, paddingTop + 4);
    
    // Axis tick labels
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillStyle = '#64748b';
    
    // X ticks
    ctx.textAlign = 'center';
    for (let i = 0; i <= xSteps; i += 2) {
      const x = paddingLeft + (plotWidth / xSteps) * i;
      const value = xMin + ((xMax - xMin) / xSteps) * i;
      ctx.fillText(Math.round(value).toString(), x, height - paddingBottom + 15);
    }
    
    // Y ticks
    ctx.textAlign = 'right';
    for (let i = 0; i <= ySteps; i += 2) {
      const y = height - paddingBottom - (plotHeight / ySteps) * i;
      const value = yMin + ((yMax - yMin) / ySteps) * i;
      ctx.fillText(Math.round(value).toString(), paddingLeft - 8, y + 4);
    }
    
    // Plot individual data points
    if (showIndividuals) {
      data.forEach(point => {
        const xVal = point.responses[xQuestion.id];
        const yVal = point.responses[yQuestion.id];
        
        if (xVal !== undefined && yVal !== undefined) {
          const x = toCanvasX(xVal);
          const y = toCanvasY(yVal);
          const size = 8;
          
          ctx.beginPath();
          ctx.arc(x, y, size, 0, 2 * Math.PI);
          ctx.fillStyle = point.color + '60'; // Semi-transparent
          ctx.fill();
          ctx.strokeStyle = point.color;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
      });
    }
    
    // Plot group averages
    if (showGroups) {
      groupAverages.forEach(group => {
        const xVal = group.avg_responses[xQuestion.id];
        const yVal = group.avg_responses[yQuestion.id];
        
        if (xVal !== undefined && yVal !== undefined) {
          const x = toCanvasX(xVal);
          const y = toCanvasY(yVal);
          const size = 25;
          
          // Halo
          ctx.beginPath();
          ctx.arc(x, y, size + 8, 0, 2 * Math.PI);
          ctx.fillStyle = group.color + '20';
          ctx.fill();
          
          // Main circle
          ctx.beginPath();
          ctx.arc(x, y, size, 0, 2 * Math.PI);
          ctx.fillStyle = group.color;
          ctx.fill();
          
          // Border
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 3;
          ctx.stroke();
          
          // Label inside circle
          ctx.fillStyle = 'white';
          ctx.font = 'bold 10px system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`n=${group.member_count}`, x, y);
          
          // Label below
          ctx.fillStyle = '#334155';
          ctx.font = '11px system-ui, sans-serif';
          ctx.fillText(group.segment_name, x, y + size + 14);
        }
      });
    }
    
    // Draw hovered point highlight
    if (hoveredPoint) {
      const xVal = hoveredPoint.responses[xQuestion.id];
      const yVal = hoveredPoint.responses[yQuestion.id];
      
      if (xVal !== undefined && yVal !== undefined) {
        const x = toCanvasX(xVal);
        const y = toCanvasY(yVal);
        
        ctx.beginPath();
        ctx.arc(x, y, 12, 0, 2 * Math.PI);
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }
    
  }, [data, groupAverages, xQuestion, yQuestion, width, height, showIndividuals, showGroups, hoveredPoint, backgroundType, quadrantConfig, diagonalConfig, paddingLeft, paddingRight, paddingTop, paddingBottom, plotWidth, plotHeight, xMin, xMax, yMin, yMax]);
  
  // Handle mouse move for hover detection
  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas || !xQuestion || !yQuestion) return;
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    
    // Helper to convert data values to canvas coordinates
    const toCanvasX = (val) => paddingLeft + ((val - xMin) / (xMax - xMin)) * plotWidth;
    const toCanvasY = (val) => height - paddingBottom - ((val - yMin) / (yMax - yMin)) * plotHeight;
    
    // Check if mouse is near any point
    let foundPoint = null;
    const threshold = 15;
    
    for (const point of data) {
      const xVal = point.responses[xQuestion.id];
      const yVal = point.responses[yQuestion.id];
      
      if (xVal !== undefined && yVal !== undefined) {
        const x = toCanvasX(xVal);
        const y = toCanvasY(yVal);
        
        const dist = Math.sqrt((mouseX - x) ** 2 + (mouseY - y) ** 2);
        if (dist < threshold) {
          foundPoint = point;
          break;
        }
      }
    }
    
    setHoveredPoint(foundPoint);
  }, [data, xQuestion, yQuestion, paddingLeft, paddingBottom, plotWidth, plotHeight, xMin, xMax, yMin, yMax, height, width]);
  
  // Handle touch for mobile
  const handleTouchMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas || !xQuestion || !yQuestion) return;
    
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    const touchX = (touch.clientX - rect.left) * scaleX;
    const touchY = (touch.clientY - rect.top) * scaleY;
    setMousePos({ x: touch.clientX - rect.left, y: touch.clientY - rect.top });
    
    const toCanvasX = (val) => paddingLeft + ((val - xMin) / (xMax - xMin)) * plotWidth;
    const toCanvasY = (val) => height - paddingBottom - ((val - yMin) / (yMax - yMin)) * plotHeight;
    
    let foundPoint = null;
    const threshold = 20;
    
    for (const point of data) {
      const xVal = point.responses[xQuestion.id];
      const yVal = point.responses[yQuestion.id];
      
      if (xVal !== undefined && yVal !== undefined) {
        const x = toCanvasX(xVal);
        const y = toCanvasY(yVal);
        
        const dist = Math.sqrt((touchX - x) ** 2 + (touchY - y) ** 2);
        if (dist < threshold) {
          foundPoint = point;
          break;
        }
      }
    }
    
    setHoveredPoint(foundPoint);
  }, [data, xQuestion, yQuestion, paddingLeft, paddingBottom, plotWidth, plotHeight, xMin, xMax, yMin, yMax, height, width]);
  
  return (
    <div className="relative">
      <canvas 
        ref={canvasRef} 
        width={width} 
        height={height}
        className="rounded-lg cursor-crosshair touch-pan-x touch-pan-y w-full h-auto"
        style={{ maxWidth: width }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredPoint(null)}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => setHoveredPoint(null)}
      />
      
      {/* Tooltip */}
      {hoveredPoint && showIndividuals && (
        <div 
          className="absolute bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none z-10"
          style={{
            left: Math.min(mousePos.x + 15, width - 150),
            top: mousePos.y - 10,
            maxWidth: 200
          }}
        >
          <div className="text-slate-300">
            <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: hoveredPoint.color }} />
            {hoveredPoint.segment_name}
          </div>
          <div className="text-slate-400 mt-1 text-[10px]">
            {xQuestion?.label}: {hoveredPoint.responses[xQuestion?.id]}
          </div>
          <div className="text-slate-400 text-[10px]">
            {yQuestion?.label}: {hoveredPoint.responses[yQuestion?.id]}
          </div>
        </div>
      )}
    </div>
  );
}

// Preset Configuration Modal
function PresetConfigModal({ 
  isOpen, 
  onClose, 
  preset, 
  questions, 
  segmentTypes, 
  segments,
  onSave,
  isNew = false 
}) {
  const [form, setForm] = useState({
    name: '',
    x_axis_question_id: '',
    y_axis_question_id: '',
    color_by_segment_type_id: '',
    filter_segment_id: '',
    show_individuals: true,
    show_groups: true,
    background_type: 'none',
    quadrant_config: {
      top_left_label: 'Keep Satisfied',
      top_left_color: '#fef3c7',
      top_right_label: 'Manage Closely',
      top_right_color: '#fecaca',
      bottom_left_label: 'Monitor',
      bottom_left_color: '#e0f2fe',
      bottom_right_label: 'Keep Informed',
      bottom_right_color: '#dcfce7',
      x_split: 50,
      y_split: 50
    },
    diagonal_config: {
      above_label: 'Above Average',
      above_color: '#dcfce7',
      below_label: 'Below Average',
      below_color: '#fecaca'
    }
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (preset) {
      setForm({
        name: preset.name || '',
        x_axis_question_id: preset.x_axis_question_id || '',
        y_axis_question_id: preset.y_axis_question_id || '',
        color_by_segment_type_id: preset.color_by_segment_type_id || '',
        filter_segment_id: preset.filter_segment_id || '',
        show_individuals: preset.show_individuals ?? true,
        show_groups: preset.show_groups ?? true,
        background_type: preset.background_type || 'none',
        quadrant_config: preset.quadrant_config || form.quadrant_config,
        diagonal_config: preset.diagonal_config || form.diagonal_config
      });
    } else if (isNew && questions.length >= 2) {
      setForm(f => ({
        ...f,
        x_axis_question_id: questions[0]?.id || '',
        y_axis_question_id: questions[1]?.id || ''
      }));
    }
  }, [preset, isNew, questions]);
  
  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a preset name');
      return;
    }
    if (!form.x_axis_question_id || !form.y_axis_question_id) {
      toast.error('Please select both X and Y axis questions');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save preset');
    } finally {
      setSaving(false);
    }
  };
  
  const filteredSegments = form.color_by_segment_type_id
    ? segments.filter(s => s.segment_type_id === form.color_by_segment_type_id)
    : segments;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Create New Preset' : 'Edit Preset'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Preset Name */}
          <div className="space-y-2">
            <Label>Preset Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Power/Interest Matrix"
            />
          </div>
          
          {/* Axis Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>X Axis (Horizontal)</Label>
              <Select 
                value={form.x_axis_question_id} 
                onValueChange={(val) => setForm(f => ({ ...f, x_axis_question_id: val }))}
              >
                <SelectTrigger><SelectValue placeholder="Select question" /></SelectTrigger>
                <SelectContent>
                  {questions.map(q => (
                    <SelectItem key={q.id} value={q.id}>{q.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Y Axis (Vertical)</Label>
              <Select 
                value={form.y_axis_question_id} 
                onValueChange={(val) => setForm(f => ({ ...f, y_axis_question_id: val }))}
              >
                <SelectTrigger><SelectValue placeholder="Select question" /></SelectTrigger>
                <SelectContent>
                  {questions.map(q => (
                    <SelectItem key={q.id} value={q.id}>{q.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Color & Filter */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Color By Segment Type</Label>
              <Select 
                value={form.color_by_segment_type_id || 'all'} 
                onValueChange={(val) => setForm(f => ({ 
                  ...f, 
                  color_by_segment_type_id: val === 'all' ? '' : val,
                  filter_segment_id: ''
                }))}
              >
                <SelectTrigger><SelectValue placeholder="All Segments" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Segment Types</SelectItem>
                  {segmentTypes.map(st => (
                    <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Filter by Segment</Label>
              <Select 
                value={form.filter_segment_id || 'all'} 
                onValueChange={(val) => setForm(f => ({ ...f, filter_segment_id: val === 'all' ? '' : val }))}
                disabled={!form.color_by_segment_type_id}
              >
                <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {filteredSegments.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Display Options */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.show_individuals}
                onChange={(e) => setForm(f => ({ ...f, show_individuals: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm">Show Individuals</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.show_groups}
                onChange={(e) => setForm(f => ({ ...f, show_groups: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm">Show Group Averages</span>
            </label>
          </div>
          
          {/* Background Type */}
          <div className="space-y-3">
            <Label>Background Style</Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, background_type: 'none' }))}
                className={`flex-1 p-3 border rounded-lg flex flex-col items-center gap-2 transition-colors ${
                  form.background_type === 'none' ? 'border-[#5AA2AE] bg-[#5AA2AE]/5' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Grid3X3 className="w-5 h-5 text-slate-400" />
                <span className="text-sm">None</span>
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, background_type: 'quadrant' }))}
                className={`flex-1 p-3 border rounded-lg flex flex-col items-center gap-2 transition-colors ${
                  form.background_type === 'quadrant' ? 'border-[#5AA2AE] bg-[#5AA2AE]/5' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <LayoutGrid className="w-5 h-5 text-slate-500" />
                <span className="text-sm">2x2 Quadrant</span>
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, background_type: 'diagonal' }))}
                className={`flex-1 p-3 border rounded-lg flex flex-col items-center gap-2 transition-colors ${
                  form.background_type === 'diagonal' ? 'border-[#5AA2AE] bg-[#5AA2AE]/5' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Slash className="w-5 h-5 text-slate-500 rotate-[-20deg]" />
                <span className="text-sm">Diagonal</span>
              </button>
            </div>
          </div>
          
          {/* Quadrant Configuration */}
          {form.background_type === 'quadrant' && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-sm">Quadrant Settings</h4>
              
              {/* Split Points */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">X Split Point (%)</Label>
                  <Input
                    type="number"
                    min="10"
                    max="90"
                    value={form.quadrant_config.x_split}
                    onChange={(e) => setForm(f => ({
                      ...f,
                      quadrant_config: { ...f.quadrant_config, x_split: parseInt(e.target.value) || 50 }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Y Split Point (%)</Label>
                  <Input
                    type="number"
                    min="10"
                    max="90"
                    value={form.quadrant_config.y_split}
                    onChange={(e) => setForm(f => ({
                      ...f,
                      quadrant_config: { ...f.quadrant_config, y_split: parseInt(e.target.value) || 50 }
                    }))}
                  />
                </div>
              </div>
              
              {/* Quadrant Labels & Colors */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'top_left', label: 'Top-Left' },
                  { key: 'top_right', label: 'Top-Right' },
                  { key: 'bottom_left', label: 'Bottom-Left' },
                  { key: 'bottom_right', label: 'Bottom-Right' }
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs">{label}</Label>
                    <div className="flex gap-2">
                      <Input
                        value={form.quadrant_config[`${key}_label`]}
                        onChange={(e) => setForm(f => ({
                          ...f,
                          quadrant_config: { ...f.quadrant_config, [`${key}_label`]: e.target.value }
                        }))}
                        placeholder="Label"
                        className="flex-1 h-8 text-sm"
                      />
                      <input
                        type="color"
                        value={form.quadrant_config[`${key}_color`]}
                        onChange={(e) => setForm(f => ({
                          ...f,
                          quadrant_config: { ...f.quadrant_config, [`${key}_color`]: e.target.value }
                        }))}
                        className="w-8 h-8 rounded cursor-pointer"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Diagonal Configuration */}
          {form.background_type === 'diagonal' && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-sm">Diagonal Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Above Diagonal</Label>
                  <div className="flex gap-2">
                    <Input
                      value={form.diagonal_config.above_label}
                      onChange={(e) => setForm(f => ({
                        ...f,
                        diagonal_config: { ...f.diagonal_config, above_label: e.target.value }
                      }))}
                      placeholder="Label"
                      className="flex-1 h-8 text-sm"
                    />
                    <input
                      type="color"
                      value={form.diagonal_config.above_color}
                      onChange={(e) => setForm(f => ({
                        ...f,
                        diagonal_config: { ...f.diagonal_config, above_color: e.target.value }
                      }))}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Below Diagonal</Label>
                  <div className="flex gap-2">
                    <Input
                      value={form.diagonal_config.below_label}
                      onChange={(e) => setForm(f => ({
                        ...f,
                        diagonal_config: { ...f.diagonal_config, below_label: e.target.value }
                      }))}
                      placeholder="Label"
                      className="flex-1 h-8 text-sm"
                    />
                    <input
                      type="color"
                      value={form.diagonal_config.below_color}
                      onChange={(e) => setForm(f => ({
                        ...f,
                        diagonal_config: { ...f.diagonal_config, below_color: e.target.value }
                      }))}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#5AA2AE] hover:bg-[#4a8a94]">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {isNew ? 'Create Preset' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MatrixViewPage() {
  const { projectId } = useParams();
  const { currentProject, isProjectAdmin, isAccountOwner } = useProject();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data from API
  const [questions, setQuestions] = useState([]);
  const [segmentTypes, setSegmentTypes] = useState([]);
  const [segments, setSegments] = useState([]);
  const [dataPoints, setDataPoints] = useState([]);
  const [groupAverages, setGroupAverages] = useState([]);
  const [legend, setLegend] = useState([]);
  const [totalStakeholders, setTotalStakeholders] = useState(0);
  
  // Presets
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('custom');
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  
  // Custom analysis controls
  const [customControlsOpen, setCustomControlsOpen] = useState(false);
  
  // Filter selections
  const [selectedSegmentType, setSelectedSegmentType] = useState('all');
  const [selectedSegment, setSelectedSegment] = useState('all');
  
  // Axis selections
  const [xAxisQuestion, setXAxisQuestion] = useState('');
  const [yAxisQuestion, setYAxisQuestion] = useState('');
  
  // Display options
  const [showIndividuals, setShowIndividuals] = useState(true);
  const [showGroups, setShowGroups] = useState(true);
  
  // Background options
  const [backgroundType, setBackgroundType] = useState('none');
  const [quadrantConfig, setQuadrantConfig] = useState(null);
  const [diagonalConfig, setDiagonalConfig] = useState(null);
  
  // Check if user is admin (project admin, account owner, or global admin)
  const canConfigure = isProjectAdmin || isAccountOwner || user?.is_global_admin;
  
  // Fetch presets
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const res = await api.get(`/projects/${projectId}/matrix-presets`);
        setPresets(res.data || []);
      } catch (err) {
        console.error('Failed to load presets:', err);
      }
    })();
  }, [projectId]);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const params = {};
        if (selectedSegmentType && selectedSegmentType !== 'all') {
          params.segment_type_id = selectedSegmentType;
        }
        if (selectedSegment && selectedSegment !== 'all') {
          params.segment_id = selectedSegment;
        }
        
        const response = await api.get(`/projects/${projectId}/matrix-analysis`, { params });
        const data = response.data;
        
        setQuestions(data.questions || []);
        setSegmentTypes(data.segment_types || []);
        setSegments(data.segments || []);
        setDataPoints(data.data_points || []);
        setGroupAverages(data.group_averages || []);
        setLegend(data.legend || []);
        setTotalStakeholders(data.total_stakeholders || 0);
        
        // Set default axis questions if not set
        if (data.questions?.length >= 2) {
          if (!xAxisQuestion) setXAxisQuestion(data.questions[0].id);
          if (!yAxisQuestion) setYAxisQuestion(data.questions[1].id);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching matrix analysis:', err);
        setError(err.response?.data?.detail || 'Failed to load matrix analysis data');
        setLoading(false);
      }
    };
    
    if (projectId) {
      fetchData();
    }
  }, [projectId, selectedSegmentType, selectedSegment]);
  
  // Set initial axis selections when questions load
  useEffect(() => {
    if (questions.length >= 2 && !xAxisQuestion && !yAxisQuestion) {
      setXAxisQuestion(questions[0].id);
      setYAxisQuestion(questions[1].id);
    }
  }, [questions, xAxisQuestion, yAxisQuestion]);
  
  // Apply preset when selected
  useEffect(() => {
    if (selectedPreset === 'custom') {
      // Reset to defaults or keep current
      setBackgroundType('none');
      setQuadrantConfig(null);
      setDiagonalConfig(null);
      return;
    }
    
    const preset = presets.find(p => p.id === selectedPreset);
    if (preset) {
      setXAxisQuestion(preset.x_axis_question_id);
      setYAxisQuestion(preset.y_axis_question_id);
      setSelectedSegmentType(preset.color_by_segment_type_id || 'all');
      setSelectedSegment(preset.filter_segment_id || 'all');
      setShowIndividuals(preset.show_individuals ?? true);
      setShowGroups(preset.show_groups ?? true);
      setBackgroundType(preset.background_type || 'none');
      setQuadrantConfig(preset.quadrant_config);
      setDiagonalConfig(preset.diagonal_config);
    }
  }, [selectedPreset, presets]);
  
  const xQuestion = questions.find(q => q.id === xAxisQuestion);
  const yQuestion = questions.find(q => q.id === yAxisQuestion);
  
  // Auto-apply preset background when non-admin user selects same X/Y as a preset
  useEffect(() => {
    if (selectedPreset !== 'custom') return; // already on a preset
    if (!xAxisQuestion || !yAxisQuestion) return;
    
    const matchingPreset = presets.find(p => 
      p.x_axis_question_id === xAxisQuestion && p.y_axis_question_id === yAxisQuestion
    );
    
    if (matchingPreset) {
      setBackgroundType(matchingPreset.background_type || 'none');
      setQuadrantConfig(matchingPreset.quadrant_config || null);
      setDiagonalConfig(matchingPreset.diagonal_config || null);
    } else {
      setBackgroundType('none');
      setQuadrantConfig(null);
      setDiagonalConfig(null);
    }
  }, [xAxisQuestion, yAxisQuestion, presets, selectedPreset]);
  
  // Filter segments by selected segment type
  const filteredSegments = selectedSegmentType && selectedSegmentType !== 'all'
    ? segments.filter(s => s.segment_type_id === selectedSegmentType)
    : segments;
  
  // Calculate statistics
  const dataPointsWithBothAxes = dataPoints.filter(
    dp => dp.responses[xAxisQuestion] !== undefined && dp.responses[yAxisQuestion] !== undefined
  );
  
  // Handlers
  const handleSavePreset = async (formData) => {
    if (editingPreset) {
      await api.put(`/projects/${projectId}/matrix-presets/${editingPreset.id}`, formData);
      setPresets(prev => prev.map(p => p.id === editingPreset.id ? { ...p, ...formData } : p));
      toast.success('Preset updated');
    } else {
      const res = await api.post(`/projects/${projectId}/matrix-presets`, formData);
      setPresets(prev => [...prev, res.data]);
      toast.success('Preset created');
    }
  };
  
  const handleDeletePreset = async (presetId) => {
    if (!confirm('Delete this preset?')) return;
    try {
      await api.delete(`/projects/${projectId}/matrix-presets/${presetId}`);
      setPresets(prev => prev.filter(p => p.id !== presetId));
      if (selectedPreset === presetId) setSelectedPreset('custom');
      toast.success('Preset deleted');
    } catch (err) {
      toast.error('Failed to delete preset');
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[500px]" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-6">
            <div className="flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (questions.length < 2) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Response Matrix
          </h1>
          <p className="text-slate-600 mt-1">
            Compare responses across multiple dimensions
          </p>
        </div>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-6">
            <div className="flex items-center gap-3 text-amber-700">
              <AlertCircle className="w-5 h-5" />
              <span>At least 2 slider questions are required for matrix analysis. Please add more questions to your project.</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Response Matrix
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">
            Compare responses across multiple dimensions
          </p>
        </div>
        
        {canConfigure && (
          <Button
            onClick={() => { setEditingPreset(null); setShowPresetModal(true); }}
            variant="outline"
            size="sm"
            className="self-start"
            data-testid="configure-preset-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configure Presets
          </Button>
        )}
      </div>
      
      {/* Summary stats */}
      <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
        <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2">
          <Users className="w-4 h-4 text-slate-500" />
          <span className="text-xs sm:text-sm font-medium">{totalStakeholders} stakeholders</span>
        </div>
        <div className="flex items-center gap-2 bg-[#5AA2AE]/10 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2">
          <TrendingUp className="w-4 h-4 text-[#5AA2AE]" />
          <span className="text-xs sm:text-sm font-medium">{dataPointsWithBothAxes.length} plotted</span>
        </div>
      </div>
      
      {/* Preset Selector */}
      <Card>
        <CardContent className="py-4">
          <div className="space-y-4">
            {/* Preset Dropdown */}
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px] space-y-2">
                <Label className="text-sm font-medium">Select View</Label>
                <Select value={selectedPreset} onValueChange={setSelectedPreset}>
                  <SelectTrigger data-testid="preset-selector" className="w-full">
                    <SelectValue placeholder="Select a preset or custom" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom Analysis</SelectItem>
                    {presets.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Edit/Delete buttons for selected preset (admin only) */}
              {canConfigure && selectedPreset !== 'custom' && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingPreset(presets.find(p => p.id === selectedPreset));
                      setShowPresetModal(true);
                    }}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeletePreset(selectedPreset)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            
            {/* Collapsible Custom Controls */}
            <Collapsible open={customControlsOpen} onOpenChange={setCustomControlsOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">
                  {customControlsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {customControlsOpen ? 'Hide custom controls' : 'Show custom controls'}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Segment Type Filter */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500 flex items-center gap-1">
                      <Palette className="w-3 h-3" /> Color By Segment Type
                    </Label>
                    <Select value={selectedSegmentType} onValueChange={(val) => {
                      setSelectedSegmentType(val);
                      setSelectedSegment('all');
                      setSelectedPreset('custom');
                    }}>
                      <SelectTrigger data-testid="segment-type-filter">
                        <SelectValue placeholder="All Segments" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Segment Types</SelectItem>
                        {segmentTypes.map(st => (
                          <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Segment Filter */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500 flex items-center gap-1">
                      <Circle className="w-3 h-3" /> Filter by Segment
                    </Label>
                    <Select 
                      value={selectedSegment} 
                      onValueChange={(val) => { setSelectedSegment(val); setSelectedPreset('custom'); }}
                      disabled={selectedSegmentType === 'all'}
                    >
                      <SelectTrigger data-testid="segment-filter">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {filteredSegments.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* X Axis */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500 flex items-center gap-1">
                      <Grid3X3 className="w-3 h-3" /> Horizontal Axis (X)
                    </Label>
                    <Select value={xAxisQuestion} onValueChange={(val) => { setXAxisQuestion(val); setSelectedPreset('custom'); }}>
                      <SelectTrigger data-testid="x-axis-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {questions.map(q => (
                          <SelectItem key={q.id} value={q.id}>{q.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Y Axis */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500 flex items-center gap-1">
                      <Grid3X3 className="w-3 h-3 rotate-90" /> Vertical Axis (Y)
                    </Label>
                    <Select value={yAxisQuestion} onValueChange={(val) => { setYAxisQuestion(val); setSelectedPreset('custom'); }}>
                      <SelectTrigger data-testid="y-axis-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {questions.map(q => (
                          <SelectItem key={q.id} value={q.id}>{q.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Display toggles */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Display Options</Label>
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showIndividuals}
                          onChange={(e) => { setShowIndividuals(e.target.checked); setSelectedPreset('custom'); }}
                          className="rounded border-slate-300"
                          data-testid="show-individuals-toggle"
                        />
                        <span className="text-sm">Individuals</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showGroups}
                          onChange={(e) => { setShowGroups(e.target.checked); setSelectedPreset('custom'); }}
                          className="rounded border-slate-300"
                          data-testid="show-groups-toggle"
                        />
                        <span className="text-sm">Group averages</span>
                      </label>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </CardContent>
      </Card>
      
      {/* Scatter Plot */}
      <Card>
        <CardContent className="p-2 sm:p-4">
          <div className="w-full">
            <div className="flex justify-center">
              {xQuestion && yQuestion ? (
                <MatrixScatterPlot
                  key={`matrix-${selectedSegmentType}-${selectedSegment}`}
                  data={dataPoints}
                  groupAverages={groupAverages}
                  xQuestion={xQuestion}
                  yQuestion={yQuestion}
                  showIndividuals={showIndividuals}
                  showGroups={showGroups}
                  backgroundType={backgroundType}
                  quadrantConfig={quadrantConfig}
                  diagonalConfig={diagonalConfig}
                  width={900}
                  height={550}
                />
              ) : (
                <div className="flex items-center justify-center h-[500px] text-slate-400">
                  Select questions for both axes to view the matrix
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Legend */}
      {legend.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-6">
              {legend.map(item => (
                <div key={item.id} className="flex items-center gap-2">
                  <div 
                    className="w-5 h-5 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm">{item.name}</span>
                  <span className="text-xs text-slate-400">({item.count})</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Large circles with borders show segment group averages (n= sample size). 
              Small semi-transparent circles show individual responses.
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Anonymity Notice */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-[#5AA2AE]/10">
              <EyeOff className="w-4 h-4 text-[#5AA2AE]" />
            </div>
            <div>
              <p className="font-medium text-sm">Anonymity Protection</p>
              <p className="text-sm text-slate-500">
                Individual responses are shown as unlabeled points (hover to see segment only). 
                Group averages are only displayed when the segment has 3 or more responses. 
                Sample size (n=) is shown on group markers.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Preset Configuration Modal */}
      <PresetConfigModal
        isOpen={showPresetModal}
        onClose={() => { setShowPresetModal(false); setEditingPreset(null); }}
        preset={editingPreset}
        questions={questions}
        segmentTypes={segmentTypes}
        segments={segments}
        onSave={handleSavePreset}
        isNew={!editingPreset}
      />
    </div>
  );
}
